import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { motion, AnimatePresence } from 'framer-motion';
import { AlertTriangle, Coins, X, Zap, ArrowRight } from 'lucide-react';
import { Button } from '../ui/button';

interface LowCreditAlertProps {
  threshold?: number; // Default 50
  criticalThreshold?: number; // Default 10
}

export const LowCreditAlert: React.FC<LowCreditAlertProps> = ({
  threshold = 50,
  criticalThreshold = 10,
}) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [isVisible, setIsVisible] = useState(false);
  const [alertLevel, setAlertLevel] = useState<'low' | 'critical'>('low');

  useEffect(() => {
    if (!user?.credits) return;

    const credits = user.credits;
    const lastDismissed = localStorage.getItem('lowCreditAlertDismissed');
    const lastDismissedTime = lastDismissed ? parseInt(lastDismissed) : 0;
    const now = Date.now();
    const oneDayInMs = 24 * 60 * 60 * 1000;

    // Don't show if dismissed in last 24 hours (unless critical)
    const recentlyDismissed = now - lastDismissedTime < oneDayInMs;

    // Critical alert (always show)
    if (credits < criticalThreshold) {
      setAlertLevel('critical');
      setIsVisible(true);
      return;
    }

    // Low alert (show if not recently dismissed)
    if (credits < threshold && !recentlyDismissed) {
      setAlertLevel('low');
      setIsVisible(true);
      return;
    }

    setIsVisible(false);
  }, [user?.credits, threshold, criticalThreshold]);

  const handleDismiss = () => {
    setIsVisible(false);
    localStorage.setItem('lowCreditAlertDismissed', Date.now().toString());
  };

  const handleBuyCredits = () => {
    setIsVisible(false);
    // Navigate to pricing page and scroll to credit packages
    navigate('/pricing#credit-packages');
  };

  if (!isVisible || !user) return null;

  const isCritical = alertLevel === 'critical';
  const credits = user.credits || 0;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[100] flex items-center justify-center p-4"
        onClick={handleDismiss}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0, y: 20 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.9, opacity: 0, y: 20 }}
          transition={{ type: 'spring', duration: 0.5 }}
          onClick={(e) => e.stopPropagation()}
          className={`relative max-w-md w-full rounded-3xl shadow-2xl overflow-hidden ${
            isCritical
              ? 'bg-gradient-to-br from-red-50 to-orange-50 dark:from-red-900/20 dark:to-orange-900/20 border-2 border-red-300 dark:border-red-700'
              : 'bg-gradient-to-br from-amber-50 to-yellow-50 dark:from-amber-900/20 dark:to-yellow-900/20 border-2 border-amber-300 dark:border-amber-700'
          }`}
        >
          {/* Close button */}
          <button
            onClick={handleDismiss}
            className="absolute top-4 right-4 p-2 rounded-full hover:bg-white/50 dark:hover:bg-black/20 transition-colors z-10"
          >
            <X className="w-5 h-5 text-gray-600 dark:text-gray-400" />
          </button>

          {/* Content */}
          <div className="p-8">
            {/* Icon */}
            <div className="flex justify-center mb-6">
              <motion.div
                animate={{
                  scale: [1, 1.1, 1],
                  rotate: [0, -5, 5, -5, 0],
                }}
                transition={{
                  duration: 2,
                  repeat: Infinity,
                  repeatDelay: 1,
                }}
                className={`w-20 h-20 rounded-full flex items-center justify-center shadow-lg ${
                  isCritical
                    ? 'bg-gradient-to-br from-red-500 to-orange-500'
                    : 'bg-gradient-to-br from-amber-500 to-yellow-500'
                }`}
              >
                {isCritical ? (
                  <AlertTriangle className="w-10 h-10 text-white" />
                ) : (
                  <Coins className="w-10 h-10 text-white" />
                )}
              </motion.div>
            </div>

            {/* Title */}
            <h2
              className={`text-2xl font-bold text-center mb-3 ${
                isCritical
                  ? 'text-red-900 dark:text-red-100'
                  : 'text-amber-900 dark:text-amber-100'
              }`}
            >
              {isCritical ? '🚨 Critical: Low Credits!' : '⚠️ Running Low on Credits'}
            </h2>

            {/* Credits display */}
            <div className="text-center mb-6">
              <div className="inline-flex items-center space-x-2 px-4 py-2 bg-white/60 dark:bg-black/20 rounded-full">
                <Coins className={`w-5 h-5 ${isCritical ? 'text-red-600' : 'text-amber-600'}`} />
                <span className="text-3xl font-bold text-gray-900 dark:text-white">
                  {credits.toFixed(0)}
                </span>
                <span className="text-sm text-gray-600 dark:text-gray-400">credits left</span>
              </div>
            </div>

            {/* Message */}
            <p
              className={`text-center mb-6 ${
                isCritical
                  ? 'text-red-800 dark:text-red-200'
                  : 'text-amber-800 dark:text-amber-200'
              }`}
            >
              {isCritical ? (
                <>
                  You're almost out of credits! Buy more now to continue using AI features without
                  interruption.
                </>
              ) : (
                <>
                  Your credits are running low. Stock up now to keep creating amazing content
                  without interruption!
                </>
              )}
            </p>

            {/* Benefits */}
            <div className="bg-white/60 dark:bg-black/20 rounded-xl p-4 mb-6">
              <div className="flex items-start space-x-3 mb-2">
                <Zap className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-gray-700 dark:text-gray-300">
                  <strong>Never run out:</strong> Buy credit packs and use them anytime
                </p>
              </div>
              <div className="flex items-start space-x-3 mb-2">
                <Zap className="w-5 h-5 text-purple-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-gray-700 dark:text-gray-300">
                  <strong>Better value:</strong> Larger packs offer more credits per dollar
                </p>
              </div>
              <div className="flex items-start space-x-3">
                <Zap className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-gray-700 dark:text-gray-300">
                  <strong>No expiration:</strong> Credits never expire, use them at your pace
                </p>
              </div>
            </div>

            {/* Actions */}
            <div className="flex flex-col sm:flex-row gap-3">
              <Button
                onClick={handleBuyCredits}
                className="flex-1 text-white shadow-lg hover:shadow-xl transition-all duration-200"
                style={{
                  background: 'linear-gradient(135deg, #30b3dd 0%, #4a298a 50%, #30b3dd 100%)',
                  backgroundSize: '200% 200%',
                  backgroundPosition: 'left center',
                  boxShadow: '0 4px 15px rgba(48, 179, 221, 0.3)',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundPosition = 'right center';
                  e.currentTarget.style.boxShadow = '0 6px 20px rgba(48, 179, 221, 0.4)';
                  e.currentTarget.style.transform = 'translateY(-2px)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundPosition = 'left center';
                  e.currentTarget.style.boxShadow = '0 4px 15px rgba(48, 179, 221, 0.3)';
                  e.currentTarget.style.transform = 'translateY(0)';
                }}
              >
                <Coins className="w-5 h-5 mr-2" />
                Buy Credit Packs
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>

              {!isCritical && (
                <Button
                  onClick={handleDismiss}
                  variant="outline"
                  className="sm:w-auto border-amber-300 dark:border-amber-700 text-amber-700 dark:text-amber-300 hover:bg-amber-100 dark:hover:bg-amber-900/30"
                >
                  Remind Me Later
                </Button>
              )}
            </div>

            {/* Upgrade suggestion */}
            <div className="mt-4 text-center">
              <p className="text-xs text-gray-600 dark:text-gray-400">
                Or{' '}
                <button
                  onClick={() => {
                    setIsVisible(false);
                    navigate('/pricing');
                  }}
                  className="text-blue-600 dark:text-blue-400 hover:underline font-medium"
                >
                  upgrade your plan
                </button>{' '}
                for monthly credits
              </p>
            </div>

            {isCritical && (
              <button
                onClick={handleDismiss}
                className="w-full mt-3 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 transition-colors"
              >
                I'll buy later
              </button>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default LowCreditAlert;
